import React, { useState } from "react";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import { Button, IconButton, Modal, Stack, StackProps, TextField, Typography } from "@mui/material";
import { ProgressOverlay } from "components";
import { useActionHook, useModals } from "hooks";
import { trim } from "lodash";
import { IApiServiceError, useAuthActions } from "store";

export const AccountInvite: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [emails, setEmails] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [emailsError, setEmailsError] = useState<string>("");

  const closeAndReset = () => {
    setIsOpen(false);
    setEmails("");
    setMessage("");
    setEmailsError("");
  };

  const { errorModal } = useModals();
  const { inviteFriendsAction } = useAuthActions();
  const { action, loading } = useActionHook(inviteFriendsAction, {
    onSuccess: closeAndReset,
    onReject: (err: IApiServiceError) => {
      const errorsKeys = Object.keys(err.errors);
      const oneOfEmailInvalid = errorsKeys.find((key) => key.includes("emails"));
      if (oneOfEmailInvalid) {
        setEmailsError(err.errors[oneOfEmailInvalid][0]);
        return;
      }

      errorModal({ msg: err.message });
    }
  });

  return (
    <>
      <IconButton
        onClick={() => setIsOpen(true)}
        sx={{
          width: "30px",
          height: "30px",
          padding: 0,
          "&:hover": { bgcolor: "#0f3a54", ".MuiSvgIcon-root": { fill: "#ffffff" } },
          borderRadius: "5px"
        }}
      >
        <GroupAddOutlinedIcon sx={{ fontSize: "18px", fill: "#92a7bc" }} />
      </IconButton>
      <Modal open={isOpen}>
        <ProgressOverlay containerSX={style} inProgress={loading}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderBottom: "1px solid #DAE0E2" }}
          >
            <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 500 }}>
              Invite others to Altius
            </Typography>
            <IconButton onClick={closeAndReset} size="sm">
              <CloseOutlinedIcon sx={{ fill: "#0A3142" }} />
            </IconButton>
          </Stack>
          <Stack gap="15px">
            <Typography sx={{ color: "#0A3142", fontSize: "16px", fontWeight: 400 }}>
              Add their email addresses and we&apos;ll send them an invite.
            </Typography>
            <TextField
              value={emails}
              onChange={(e) => {
                if (emailsError) {
                  setEmailsError("");
                }
                setEmails(e.target.value);
              }}
              placeholder="mail@domain.com, mail@domain.com..."
              label="Email Addresses"
              error={Boolean(emailsError)}
              helperText={emailsError}
            />
            <TextField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              multiline
              placeholder="Add a note to your invitation."
            />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" gap="18px">
            <Button variant="outlined" onClick={closeAndReset}>
              Cancel
            </Button>
            <Button
              disabled={!emails || Boolean(emailsError)}
              variant="contained"
              onClick={() =>
                action({
                  message,
                  emails: emails.split(", ").map(trim).filter(Boolean)
                })
              }
            >
              Invite
            </Button>
          </Stack>
        </ProgressOverlay>
      </Modal>
    </>
  );
};

const style: StackProps["sx"] = {
  position: "absolute" as "absolute",
  display: "flex",
  flexDirection: "column",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  outline: "none",
  bgcolor: "white",
  width: "457px",
  p: "30px",
  gap: "20px",
  borderRadius: "8px"
};
